// Type-safe API client. All methods return Promise<T> and throw ApiError on
// non-2xx responses. 401 auto-redirects to /login.

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (res.status === 401) {
    if (!location.pathname.startsWith('/login')) {
      window.location.href = '/login'
    }
    throw new ApiError(401, 'unauthorized')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, (body as { error?: string } | null)?.error ?? res.statusText, body)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

// apiFetch is a typed wrapper used by llm.ts (and future feature modules) so
// that callers don't have to JSON.stringify themselves. Pass `body` as a plain
// object — it gets serialised iff the request has a method other than GET.
export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  headers?: Record<string, string>
}

export function apiFetch<T>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    signal: opts.signal,
    headers: opts.headers,
  }
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body)
  }
  return request<T>(path, init)
}

// ─── Types ────────────────────────────────────────────────────────────────

export interface User {
  id: number
  email: string
  name: string
}

export interface Stats {
  users: number
  active_sessions: number
}

export interface VersionInfo {
  commit: string
  built_at: string
}

// ─── API ──────────────────────────────────────────────────────────────────

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<User>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<User>('/auth/me'),

  // Meta
  health: () => request<{ ok: boolean }>('/api/health'),
  version: () => request<VersionInfo>('/api/version'),
  stats: () => request<Stats>('/api/stats'),
}
